import { useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useModalLayer } from "../modals/modalHooks";
import Button from "./Button";
import "../modals/Confirm.css";
import "./Modal.css";

/*
  Modal Component

  A dialog rendered into document.body, so no card, scroll area or transformed
  parent can clip or mis-position it (it is fixed to the viewport). It uses the
  same backdrop, focus handling, Escape handling (top-most dialog only) and
  scroll lock as the other LUD modals (modalHooks).

  title        heading, also the dialog's accessible name
  description  optional text under the heading
  children     body: scrolls inside the dialog, the footer stays visible
  footer       actions row (buttons)
  size         sm / md / lg (maximum width; always at most the viewport)
  busy         while true Escape, the backdrop and the close button are ignored
  closeLabel   when given, a close button is shown with this accessible name
  onClose      called by Escape, the backdrop and the close button
*/

const Modal = ({
  title,
  description,
  children,
  footer,
  onClose,
  size = "md",
  busy = false,
  closeLabel,
  closeOnBackdrop = true,
  role = "dialog",
  className = "",
  ...props
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const ref = useRef(null);

  useModalLayer(true, { containerRef: ref, onEscape: () => !busy && onClose?.() });

  return createPortal(
    <div
      className="confirm-container ui-modal-backdrop"
      onMouseDown={(event) => closeOnBackdrop && event.target === event.currentTarget && !busy && onClose?.()}
    >
      <div
        ref={ref}
        className={`ui-modal ${size} ${className}`}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        aria-busy={busy || undefined}
        tabIndex={-1}
        {...props}
      >
        <header className="ui-modal-header">
          <div className="ui-modal-heading">
            <h4 id={titleId}>{title}</h4>
            {description && <p id={descriptionId}>{description}</p>}
          </div>
          {closeLabel && (
            <Button variant="neutral" appearance="ghost" size="sm" icon={<X size={16} />} aria-label={closeLabel} title={closeLabel} disabled={busy} onClick={onClose} />
          )}
        </header>
        <div className="ui-modal-body">{children}</div>
        {footer && <footer className="ui-modal-footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
